/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";


/** Utilities */
import { encryptUrlData } from "../../services/CryptoService";

/** Custom Hooks */
import useDebounce from "../../app/customHooks/useDebounce";

/** Common Components */
import Avatar from "../../components/ui/avatar/Avtar";
import CardSection from "../../components/ui/card/CardSection";
import Iconify from "../../layouts/components/iconify/iconify";
import Pagination from "../../components/ui/pagination/Pagination";
import FinalMolGrid from "../../components/FinalMolGrid/FinalMolGrid";

/** Common Services & Data Files */
import SwalAlert from "../../services/swalService/SwalService";
import ToastService from "../../services/toastService/ToastService";

/** Configuration Files */
import { UserGridConfig } from "./features/config/User.data";

/** RTK Query */
import {
  useDeleteUserMutation,
  useGetUsersMutation,
  useUpdateUserByStatusMutation,
} from "../../app/services/userAPI";

/** CSS Files */
import "./UserList.scss";
import { DropdownWrapper } from "../../components/ui/dropdownwrapper/DropdownWrapper";
import ToggleButton from "../../components/finalForms/ui/inputs/toggleButton/ToggleButton";
import Checkbox from "../../components/finalForms/ui/inputs/checkBox/CheckBox";
import { cleanDigitSectionValue } from "@mui/x-date-pickers/internals/hooks/useField/useField.utils";
import SearchBar from "../../components/ui/searchBar/SearchBar";
import Buttons from "../../components/ui/button/Buttons";


const UserList = () => {
  const molGridRef = useRef();
  const navigate = useNavigate();
  const { confirm } = SwalAlert();

  /* State */
  const [dataSource, setDataSource] = useState([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);
  const [viewType, setViewType] = useState(
    localStorage.getItem("viewType") || "list"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showInactive, setShowInactive] = useState(false);

  const organizationId = useSelector((state) => state.auth.user.organizationId);



  const showListView = () => {
    setViewType("list");
    localStorage.setItem("viewType", "list");
  };
  const showCardView = () => {
    setViewType("card");
    localStorage.setItem("viewType", "card");
  };

  // API Calls
  const [
    getUserList,
    { isLoading: isListLoading, isSuccess: isListSuccess, data: UserData },
  ] = useGetUsersMutation();

  const [
    updateUserByStatus,
    {
      isSuccess: isSuccupdateUserByStatusess,
      data: updateUserByStatusData,
    },
  ] = useUpdateUserByStatusMutation();

  const [deleteUser, { isSuccess: isDeleteSuccess, data: UserDataDelete }] =
    useDeleteUserMutation();

  // Functions
  const getUser = (pageObject) => {
    getUserList(pageObject);
    setDataSource([])
  };

  const handlePageChange = (page) => {
    const newPage = page.pageNumber || page;
    const newPageSize = page.pageSize || pageSize;

    setCurrentPage(newPage);
    setPageSize(newPageSize);

    localStorage.setItem("currentPage", newPage);
    localStorage.setItem("pageSize", newPageSize);

    const request = {
      pagination: { pageNumber: newPage, pageSize: newPageSize },
      filters: {
        searchText: debouncedSearch,

      },
      organizationId, showInactive: showInactive
    };
    getUser(request);
  };

  const handleOnSearchChange = (e) => {
    let value = e.target.value;
    setSearchText(value);
  };

  const handleColumnsChange = (fieldName, rowData, rowIndex) => {

    updateUserByStatus({ userId: rowData.userId, isActive: rowData.isActive });
  };

  const handleFilter = (dataField, newValue) => {
    const includeInactive = Boolean(newValue);

    setShowInactive(includeInactive);
    setCurrentPage(1);
    getUser({
      pagination: { pageNumber: 1, pageSize },
      filters: { searchText: debouncedSearch },
      organizationId,
      showInactive: includeInactive,
    });
  };


  const addUsers = () => {
    navigate("/admin/AddUser");
  };

  const onEditAction = (data) => {
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("pageSize", pageSize);
    navigate(`/admin/EditUser/${encryptUrlData(data.userId)}`);
  };

  const handleStatusToggle = (user) => {
    const updatedStatus = !user.isActive;
    updateUserByStatus({ userId: user.userId, isActive: updatedStatus });

    setDataSource((prevDataSource) =>
      prevDataSource.map((u) =>
        u.userId === user.userId ? { ...u, isActive: updatedStatus } : u
      )
    );
  };

  const onDeleteAction = (data) => {
    confirm(
      "Delete?",
      "Are you sure you want to Delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        deleteUser(data.userId);
      }
    });
  };

  const actionHandler = {
    EDIT: onEditAction,
    DELETE: onDeleteAction,
  };

  /** useEffect's **/
  useEffect(() => {
    if (isListSuccess && UserData) {
      if (UserData) {
        setDataSource(UserData.dataSource);
      }
      if (UserData.totalRecord) {
        setTotalRowCount(UserData.totalRecord);
      }
    }
  }, [isListSuccess, UserData]);

  useEffect(() => {
    if (molGridRef.current) {
      const currentPageObject = molGridRef.current.getCurrentPageObject();
      const request = {
        pagination: {
          pageNumber: currentPageObject.pageNumber,
          pageSize: currentPageObject.pageSize,
        },
        filters: {
          searchText: debouncedSearch,

        },
        organizationId: organizationId, showInactive: showInactive
      };
      getUser(request);
    }
  }, [debouncedSearch, pageSize]);

  useEffect(() => {
    if (viewType === "card" && !dataSource.length) {
      getUser({
        pagination: { pageNumber: currentPage, pageSize: pageSize },
        filters: { searchText: debouncedSearch },
        organizationId,
        showInactive,
      });
    }
  }, [viewType, currentPage, debouncedSearch]);

  useEffect(() => {
    if (isSuccupdateUserByStatusess && updateUserByStatusData) {
      if (updateUserByStatusData.errorMessage.includes("exists")) {
        ToastService.warning(updateUserByStatusData.errorMessage);
        return;
      } else {
        ToastService.success(updateUserByStatusData.errorMessage);
      }
    }
  }, [isSuccupdateUserByStatusess, updateUserByStatusData]);

  useEffect(() => {
    if (isDeleteSuccess && UserDataDelete) {
      ToastService.success(UserDataDelete.errorMessage);
      handlePageChange({ pageNumber: currentPage, pageSize });
    }
  }, [isDeleteSuccess && UserDataDelete]);



  const customFilter = () => {
    return (



      <div className="d-flex align-items-center w-100 justify-content-between mr-2" >

        <div className="section-title mb-0">
          <Iconify icon={"bi bi-people-fill"} />
          User List
        </div>
        <div className="w-110">
          <Checkbox
            label="Include Inactive"
            name="showInactive"
            dataField="showInactive"
            onChange={handleFilter}
            keyId=""
            checked={showInactive}

          />
        </div>

        <div className="mr-2 w-25">
          <SearchBar
            handleChange={handleOnSearchChange}
            searchValue={debouncedSearch}
            searchText={"Search via UserName"}
          />
        </div>
        <div className="shorting-tabs mr-2">
          <div
            className={`view-items grid-list ${viewType === "list" ? "active" : ""
              }`}
            title="List View"
            onClick={showListView}
          >
            <Iconify icon="line-md:list-3-filled" />
          </div>
          <div
            className={`view-items card-list ${viewType === "card" ? "active" : ""
              }`}
            title="Card View"
            onClick={showCardView}
          >
            <Iconify icon="mdi:view-grid" />
          </div>
        </div>
        <div>
          <Buttons

            buttonTypeClassName={"theme-btn"}
            onClick={addUsers}
            buttonText={"User"}
            textWithIcon={true}
            iconName={"material-symbols:add-rounded"}
            fontIcon={true}
          />
        </div>

      </div>
    )
  }


  return (
    <div className="user-list center-top-space">
      <div className="list-view-section">
        <div className="table-grid">
          <CardSection
            cardTitle={"User List"}
            buttonClassName="theme-btn"
            titleButtonClick={addUsers}
            buttonText="User"
            textWithIcon={true}
            iconName="material-symbols:add-rounded"
            fontIcon={true}
            showListView={showListView}
            showCardView={showCardView}
            viewType={viewType}
            customHeader={true}
            headerContentWrapper={customFilter()}
          >

            {viewType === "card" ? (
              <div className="card-view">
                <div className="row">
                  {dataSource.map((user, index) => {
                    const statusClass = user.isActive ? "active" : "inactive";

                    return (
                      <div className="user-card-responsive" key={user.userId}>
                        <div className="user-card justify-content-between">
                          <div className="user-card-main d-flex align-items-start">
                            <div className="card-profile">
                              <div className="user-desc-status">
                                <div
                                  className={`statusButton ${statusClass}`}
                                ></div>
                              </div>
                              {/* Render Avatar */}
                              <div className="avatar-name">
                                <Avatar
                                  src=""
                                  alt={user.fullName}
                                  size="medium"
                                  shape="circle"
                                />
                                <div className="user-info d-flex">
                                  <span className="user-name">
                                    {user.fullName}
                                  </span>
                                  {user.isSupperAdmin ? (
                                    <div
                                      className="super-admin-badge"
                                      title="Is Super User"
                                    >
                                      <Iconify icon="flowbite:badge-check-solid" />
                                    </div>
                                  ) : (
                                    ""
                                  )}
                                </div>
                              </div>
                              <div className="action-btn">
                                <ToggleButton
                                  isChecked={user.isActive}
                                  isdisable={false}
                                  onChange={() => handleStatusToggle(user)}
                                />
                                <DropdownWrapper
                                  direction="left"
                                  className="action-button-dropdown"
                                  toggleElement={
                                    <button className="customer-more-btn">
                                      <Iconify icon="humbleicons:dots-vertical" />
                                    </button>
                                  }
                                >
                                  <button onClick={() => onEditAction(user)}>
                                    <Iconify icon="line-md:pencil" />
                                    Edit
                                  </button>
                                  <button onClick={() => onDeleteAction(user)}>
                                    <Iconify icon="mingcute:delete-2-line" />
                                    Delete
                                  </button>
                                </DropdownWrapper>
                              </div>
                            </div>
                            <div className="user-desc">
                              <div className="email-address">
                                <Iconify icon="mdi:email-outline" /> Email :{" "}
                                {user.emailAddress}
                              </div>

                              <div className="contact-number">
                                <Iconify icon="mdi:account-supervisor" />
                                Role : {user.roleName}
                              </div>
                              <div className="contact-number">
                                <Iconify icon="solar:user-bold-duotone" />
                                Rpt. Manager :{" "}
                                {user.reportingManagerName
                                  ? user.reportingManagerName
                                  : "-"}
                              </div>
                              <div className="gender-badge"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <FinalMolGrid
                ref={molGridRef}
                configuration={UserGridConfig}
                dataSource={dataSource}
                allowPagination={true}
                pagination={{
                  totalCount: totalRowCount,
                  currentPage,
                  pageSize,
                }}
                isLoading={isListLoading}
                onActionChange={actionHandler}
                onColumnChange={handleColumnsChange}
                onPageChange={handlePageChange}
              />
            )}
          </CardSection>
          {viewType === "card" ? (
            <div className="pagination-container">
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalRowCount}
                onPageChange={handlePageChange}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default UserList;
